"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/components/ui/use-toast';
import { Session } from '@supabase/supabase-js';
import RichTextEditor from '@/components/RichTextEditor';
import ModelAutocomplete, { BrandAutocomplete } from '@/components/ModelAutocomplete';
import { Loader2, Gavel, DollarSign, X, Package, Info, Image, FileText, Store, MapPin, Truck, Clock } from '@/components/icons';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, addDays, setHours, setMinutes, setSeconds, setMilliseconds } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import ProductImageManager from './ProductImageManager';
import ProductTypeahead from './ProductTypeahead';
import NewProductWizard from './NewProductWizard';
import BarcodeScannerModal from './BarcodeScannerModal';
import { ProductItem } from '@/lib/supabase';
import LocationInput from './LocationInput';
import type { NoteLocation } from '@/types/location';
import { updateProduct, getProductById } from '@/lib/supabase';
import { getUserStores } from '@/lib/supabase/stores';
import { getCatalogByBarcode, getCatalogById, getProductAvailability } from '@/lib/supabase/products';
import AuctionFields from './product-form/AuctionFields';
import FixedPriceFields from './product-form/FixedPriceFields';
import VehicleDetailsForm from './product-form/VehicleDetailsForm';
import ElectronicsDetailsForm from './product-form/ElectronicsDetailsForm';
import PrivateClassesFields from './product-form/PrivateClassesFields';
import BarcodeScanner from './BarcodeScanner';

/**
 * Determina el tipo de transporte requerido basado en el peso del producto
 * @param weightKg Peso en kilogramos
 * @returns Tipo de transporte: 'walking', 'bike', 'motorcycle', 'car', 'van'
 */
const getRequiredTransportType = (weightKg: number): string => {
  if (weightKg <= 0) return 'walking';
  if (weightKg <= 5) return 'bike'; // Hasta 5kg: bicicleta
  if (weightKg <= 15) return 'motorcycle'; // 5-15kg: moto
  if (weightKg <= 50) return 'car'; // 15-50kg: auto
  return 'van'; // Más de 50kg: camioneta
};

interface ProductFormProps {
  session?: Session | null;
  profile?: any;
  initialProduct?: ProductItem | null;
  onSubmit?: (
    productData: Omit<ProductItem, 'id' | 'created_at' | 'updated_at' | 'seller_id' | 'profiles' | 'current_bid' | 'highest_bidder_id'>,
    imagesToDelete: string[]
  ) => Promise<void>;
  loading?: boolean;
  submitButtonText?: string;
  onTitleChange?: (title: string) => void;
  hideTitleInput?: boolean;
  hideTypeSelector?: boolean;
  activeStep?: number;
  initialProductType?: string | null;
  initialBarcode?: string;
  initialStoreId?: string | null;
  // NUEVO: modo single-step (renderiza todo en una sola vista)
  singleStep?: boolean;
  // NUEVO: productType controlado externamente
  productType?: string;
}

const ProductForm: React.FC<ProductFormProps> = ({
  session,
  profile,
  initialProduct,
  onSubmit,
  loading = false,
  submitButtonText = "Publicar producto",
  onTitleChange,
  hideTitleInput = false,
  hideTypeSelector = false,
  activeStep,
  initialProductType = null,
  initialBarcode,
  initialStoreId = null,
  singleStep = false,
  productType: externalProductType,
}) => {
  const [title, setTitle] = useState('');
  const [titleLockedFromBarcode, setTitleLockedFromBarcode] = useState(false);
  const [contentQuantity, setContentQuantity] = useState<string>('');
  const [contentUnit, setContentUnit] = useState<'gr' | 'ml'>('gr');
  const [author, setAuthor] = useState<string | null>('');
  const [description, setDescription] = useState('');
  const [productImages, setProductImages] = useState<string[]>([]);
  const [mainImageUrl, setMainImageUrl] = useState<string | null>(null);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [fixedRentalPrice, setFixedRentalPrice] = useState('');
  const [fixedSalePrice, setFixedSalePrice] = useState('');
  const [freeShipping, setFreeShipping] = useState(false);
  const [weight, setWeight] = useState<string>(''); // Peso en kg para determinar tipo de vehículo requerido
  // Store-specific availability inputs (separate from fixedSale/fixedRental which are product-level)
  const [storeStock, setStoreStock] = useState('1');
  const [storePrice, setStorePrice] = useState('');
  const [isAuction, setIsAuction] = useState(false);
  // Set auction start time to "now" by default
  const [auctionStartTime, setAuctionStartTime] = useState<Date | undefined>(() => 
    setMilliseconds(setSeconds(setMinutes(setHours(new Date(), new Date().getHours()), new Date().getMinutes()), 0), 0)
  );
  const [auctionEndTime, setAuctionEndTime] = useState<Date | undefined>();
  const [startingBid, setStartingBid] = useState('');
  const [buyNowPrice, setBuyNowPrice] = useState('');
  const [productType, setProductType] = useState(externalProductType || initialProductType || 'book');
  const [departureTime, setDepartureTime] = useState<string>(initialProduct?.details?.departure_time || '');
  const [condition, setCondition] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, any>>({});
  const [rentalOptions, setRentalOptions] = useState<Array<{ days: number; price: string }>>([]);
  const [userStores, setUserStores] = useState<Array<any>>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  // Sincronizar ubicación del producto con la tienda seleccionada
  useEffect(() => {
    if (!selectedStoreId || !userStores || userStores.length === 0) return;
    const store = userStores.find((s) => s.id === selectedStoreId);
    if (store) {
      if (store.latitude != null && store.longitude != null) {
        setLatitude(store.latitude);
        setLongitude(store.longitude);
        setLocationName(store.location_display_name || null);
      } else if (profile?.relevant_latitude != null && profile?.relevant_longitude != null) {
        setLatitude(profile.relevant_latitude);
        setLongitude(profile.relevant_longitude);
        setLocationName(profile.relevant_location_display_name || null);
      } else {
        setLatitude(null);
        setLongitude(null);
        setLocationName(null);
      }
      // Si el editor de ubicación está abierto, ciérralo y limpia el buffer
      setShowLocationEditor(false);
      setEditingLocationName(null);
      setEditingLatitude(null);
      setEditingLongitude(null);
      setLocationEditorAutoFocus(false);
    }
  }, [selectedStoreId, userStores, profile?.relevant_latitude, profile?.relevant_longitude, profile?.relevant_location_display_name]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  // Location fields (set from user profile by default)
  const [latitude, setLatitude] = useState<number | null>(profile?.relevant_latitude || null);
  const [longitude, setLongitude] = useState<number | null>(profile?.relevant_longitude || null);
  const [locationName, setLocationName] = useState<string | null>(profile?.relevant_location_display_name || null);
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [showNewProductWizard, setShowNewProductWizard] = useState(false);
  const [newWizardQuery, setNewWizardQuery] = useState<string | null>(null);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [barcodeModalInput, setBarcodeModalInput] = useState<string>('');
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [catalogData, setCatalogData] = useState<any | null>(null);
  const isCatalogProduct = Boolean((initialProduct && (initialProduct as any).product_catalog_id) || (catalogData && catalogData.id));
  const imagesSectionRef = React.useRef<HTMLDivElement | null>(null);
  const availabilitySectionRef = React.useRef<HTMLDivElement | null>(null);
  const [locationEditorAutoFocus, setLocationEditorAutoFocus] = useState(false);
  const [editingLatitude, setEditingLatitude] = useState<number | null>(null);
  const [editingLongitude, setEditingLongitude] = useState<number | null>(null);
  const [editingLocationName, setEditingLocationName] = useState<string | null>(null);
  const { toast } = useToast();
  const [invalidField, setInvalidField] = useState<string | null>(null);
  // Transient highlight + tooltip for UX: appears briefly and then disappears
  const [highlightField, setHighlightField] = useState<string | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  

  useEffect(() => {
    // Fetch user's stores for store-scoped listing
    (async () => {
      try {
        if (session?.user?.id) {
          const stores = await getUserStores();
          setUserStores(stores || []);
          if (initialStoreId) {
            setSelectedStoreId(initialStoreId);
          } else if (stores && stores.length === 1) {
            setSelectedStoreId(stores[0].id);
          }
        }
      } catch (e) {
        console.warn('ProductForm: failed to fetch user stores', e);
      }
    })();

    const defaultStartTime = setMilliseconds(setSeconds(setMinutes(setHours(new Date(), new Date().getHours()), new Date().getMinutes()), 0), 0);
    const defaultEndTime = setMilliseconds(setSeconds(setMinutes(setHours(addDays(new Date(), 2), 23), 59), 59), 999);

    if (initialProduct) {
      setSelectedStoreId(initialProduct.store_id || null);
      setTitle(initialProduct.title);
      setAuthor(initialProduct.author);
      setDescription(initialProduct.description || '');
      setProductImages(initialProduct.image_urls || []);
      setMainImageUrl(initialProduct.main_image_url || initialProduct.cover_url);
      setFixedRentalPrice(initialProduct.fixed_rental_price?.toString() || '');
      setFixedSalePrice(initialProduct.fixed_sale_price?.toString() || '');
      // Usar solo products.stock y products.fixed_sale_price
      if (typeof initialProduct.stock !== 'undefined' && initialProduct.stock !== null) {
        setStoreStock(String(initialProduct.stock));
      } else {
        setStoreStock('1'); // Default to 1 if no stock is set
      }
      if (typeof initialProduct.fixed_sale_price !== 'undefined' && initialProduct.fixed_sale_price !== null) {
        setStorePrice(`$${Number(initialProduct.fixed_sale_price).toLocaleString('es-AR')}`);
      } else {
        setStorePrice('$');
      }
      setFreeShipping(initialProduct.free_shipping);
      setWeight((initialProduct as any).weight?.toString() || '');
      setIsAuction(initialProduct.is_auction);
      // Set start time to "now" if not defined
      setAuctionStartTime(initialProduct.auction_start_time ? new Date(initialProduct.auction_start_time) : defaultStartTime);
      setAuctionEndTime(initialProduct.auction_end_time ? new Date(initialProduct.auction_end_time) : defaultEndTime);
      setStartingBid(initialProduct.starting_bid?.toString() || '');
      setBuyNowPrice(initialProduct.buy_now_price?.toString() || '');
      setProductType(initialProduct.product_type);
      setCondition(initialProduct.condition);
      setDetails(initialProduct.details || {});
      // initialize rentalOptions from product.rental_options (new column) or fallback to details.rental_options
      if (Array.isArray((initialProduct as any).rental_options)) {
        setRentalOptions((initialProduct as any).rental_options.map((opt: any) => ({ days: opt.days || 1, price: opt.price ? String(opt.price) : '' })));
      } else if (initialProduct.details && Array.isArray(initialProduct.details.rental_options)) {
        setRentalOptions(initialProduct.details.rental_options.map((opt: any) => ({ days: opt.days || 1, price: opt.price ? String(opt.price) : '' })));
      } else {
        setRentalOptions([]);
      }
      setImagesToDelete([]);
      // Initialize location fields from the product if available, otherwise leave current profile-based defaults
      if (typeof initialProduct.latitude !== 'undefined' && initialProduct.latitude !== null) {
        setLatitude(initialProduct.latitude);
      } else if (profile?.relevant_latitude != null) {
        setLatitude(profile.relevant_latitude);
      } else {
        setLatitude(null);
      }

      if (typeof initialProduct.longitude !== 'undefined' && initialProduct.longitude !== null) {
        setLongitude(initialProduct.longitude);
      } else if (profile?.relevant_longitude != null) {
        setLongitude(profile.relevant_longitude);
      } else {
        setLongitude(null);
      }

      if (typeof initialProduct.location_name !== 'undefined' && initialProduct.location_name !== null) {
        setLocationName(initialProduct.location_name);
      } else if (profile?.relevant_location_display_name) {
        setLocationName(profile.relevant_location_display_name);
      } else {
        setLocationName(null);
      }
      // If this product is linked to a catalog entry, fetch catalog data and lock certain fields
      const catalogId = (initialProduct as any).product_catalog_id ?? null;
      if (catalogId) {
        (async () => {
          try {
            const c = await getCatalogById(String(catalogId));
            if (c) {
              setCatalogData(c);
              // Override UI fields with catalog data for display (read-only)
              if (c.title) setTitle(c.title);
              if (c.details && c.details.description) setDescription(c.details.description);
              else if (c.description) setDescription(c.description || '');
              
              // Only overwrite images if the product doesn't have its own images yet
              if (!initialProduct.image_urls || initialProduct.image_urls.length === 0) {
                setProductImages(c.image_urls || []);
              }
              if (!initialProduct.main_image_url && !initialProduct.cover_url) {
                setMainImageUrl(c.main_image_url || null);
              }

              // set brand/model/content into details if present in catalog
              setDetails(prev => ({ ...(prev || {}), brand: c.brand ?? prev.brand, model: c.model ?? prev.model, content: c.content ?? prev.content }));
              // Lock title from barcode auto-building behavior
              setTitleLockedFromBarcode(true);

              // Try to fetch seller-specific availability (price/stock) for this catalog entry
              try {
                const sellerId = session?.user?.id;
                if (sellerId) {
                  const avail = await getProductAvailability(String(catalogId), sellerId);
                  if (avail) {
                    if (typeof avail.stock !== 'undefined' && avail.stock !== null) setStoreStock(String(avail.stock));
                    if (typeof avail.price !== 'undefined' && avail.price !== null) setStorePrice(`$${Number(avail.price).toLocaleString('es-AR')}`);
                  }
                }
              } catch (avErr) {
                console.warn('ProductForm: failed to fetch availability for catalog product', avErr);
              }
            }
          } catch (e) {
            console.warn('ProductForm: failed to fetch catalog data', e);
          }
        })();
      }
    } else {
      setTitle('');
      setTitleLockedFromBarcode(false);
      setAuthor('');
      setDescription('');
      setProductImages([]);
      setMainImageUrl(null);
      setFixedRentalPrice('');
      setFixedSalePrice('');
      setStoreStock('1'); // Default to 1
      setStorePrice('$');
      setFreeShipping(false);
      setWeight('');
      setIsAuction(false);
      setAuctionStartTime(defaultStartTime);
      setAuctionEndTime(defaultEndTime);
      setStartingBid('');
      setBuyNowPrice('');
      // Preserve productType if it was set externally or explicitly, otherwise use initialProductType or current productType
      setProductType(initialProductType ?? externalProductType ?? productType);
      setCondition(null);
      setDetails({});
      setRentalOptions([]);
      setImagesToDelete([]);
    }
  }, [initialProduct, profile]);

  


  // Permitir que el productType sea controlado externamente (singleStep)
  useEffect(() => {
    if (externalProductType) {
      setProductType(externalProductType);
    } else if (initialProductType) {
      setProductType(initialProductType);
    }
  }, [externalProductType, initialProductType]);

  // Pre-fill from barcode if provided (on mount)
  useEffect(() => {
    if (initialBarcode && productType === 'groceries') {
      handleBarcodePreFill();
    }
  }, [initialBarcode, productType]);

  // Auto-hide transient highlight + tooltip after a short timeout
  useEffect(() => {
    if (!highlightField) return;
    setTooltipVisible(true);
    const t = setTimeout(() => {
      setHighlightField(null);
      setTooltipVisible(false);
    }, 2800);
    return () => clearTimeout(t);
  }, [highlightField]);

  const handleBarcodePreFill = async () => {
    if (!initialBarcode) return;
    try {
      const catalogItem = await getCatalogByBarcode(initialBarcode);
      if (catalogItem) {
        // Found in catalog - pre-fill with catalog data
        setCatalogData(catalogItem);
        setTitle(catalogItem.title || '');
        // persist barcode into details so it's stored on create
        setDetails(prev => ({ ...prev, barcode: initialBarcode }));
        if (catalogItem.brand) setDetails(prev => ({ ...prev, brand: catalogItem.brand }));
        if (catalogItem.model) setDetails(prev => ({ ...prev, model: catalogItem.model }));
        if (catalogItem.content) {
          // Parse content like "200gr" or "500ml"
          const match = (catalogItem.content || '').match(/^(\d+(?:\.\d+)?)\s*(gr|ml)$/i);
          if (match) {
            setContentQuantity(match[1]);
            setContentUnit(match[2].toLowerCase() === 'ml' ? 'ml' : 'gr');
          } else {
            setContentQuantity(catalogItem.content || '');
          }
        }
        setTitleLockedFromBarcode(true);
      }
    } catch (e) {
      console.error('Error pre-filling from barcode:', e);
    }
  };

  // Notify parent when title changes
  useEffect(() => {
    if (onTitleChange) onTitleChange(title);
  }, [title, onTitleChange]);

  // Keep title automatically built for vehicles: 'Brand Name Content' (with sensible fallbacks)
  useEffect(() => {
    if (productType === 'vehicle') {
      const brand = details.brand ? String(details.brand).trim() : '';
      const productName = title ? String(title).trim() : '';
      const content = contentQuantity ? `${contentQuantity}${contentUnit}` : '';
      const parts = [] as string[];
      if (brand) parts.push(brand);
      if (productName) parts.push(productName);
      if (content) parts.push(content);
      const built = parts.join(' ').trim();
      if (built) {
        setTitle(built);
      }
    }
  }, [productType, details.brand, contentQuantity, contentUnit]);

  // Auto-build title for groceries: 'Brand ProductName Content'
  useEffect(() => {
    if (productType === 'groceries' && !titleLockedFromBarcode) {
      const brand = details.brand ? String(details.brand).trim() : '';
      const catalogTitle = title ? String(title).trim() : '';
      const content = contentQuantity ? `${contentQuantity}${contentUnit}` : '';
      
      // Only auto-build title when we have a brand or an existing catalogTitle
      const shouldBuild = Boolean(brand || catalogTitle || details.model);
      if (shouldBuild) {
        // Avoid duplicating brand if catalogTitle already starts with it
        const brandLower = brand.toLowerCase();
        const titleStartsWithBrand = brand && catalogTitle.toLowerCase().startsWith(brandLower);
        const parts = [] as string[];
        if (brand && !titleStartsWithBrand) parts.push(brand);
        if (catalogTitle) parts.push(catalogTitle);
        if (content) parts.push(content);
        const autoTitle = parts.join(' ').trim();
        if (autoTitle && autoTitle !== title) {
          setTitle(autoTitle);
        }
      }
    }
  }, [productType, details.brand, title, contentQuantity, contentUnit, titleLockedFromBarcode, details.model]);

  // Lock condition to 'new' for groceries (Paso 1 de almacén)
  useEffect(() => {
    if (productType === 'groceries') {
      setCondition('new');
    }
  }, [productType]);

  const handleImagesChange = useCallback((
    allImageUrls: string[],
    newMainImageUrl: string | null,
    deletedImages: string[]
  ) => {
    setProductImages(allImageUrls);
    setMainImageUrl(newMainImageUrl);
    setImagesToDelete(deletedImages);
    // Clear image-related validation highlight when user adds images
    if (invalidField === 'images') {
      setInvalidField(null);
    }
    if (highlightField === 'images') {
      setHighlightField(null);
      setTooltipVisible(false);
    }
  }, [invalidField, highlightField]);

  const handlePriceInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    setter(rawValue);
  };

  const validateAndSubmit = async () => {
    // Enhanced validation: focus & highlight first missing field
    const missing: string[] = [];
    if (!session?.user?.id) {
      toast({ title: "Error", description: "Debes iniciar sesión para publicar.", variant: "destructive" });
      return;
    }
    
    
    // If product is groceries, no fields are required — skip missing-field checks
    if (productType !== 'groceries') {
      // Only require title if the title input is rendered
      if (!(hideTitleInput) && !title.trim()) missing.push('title');
      // Only require author for books
      if (productType === 'book' && !author?.trim()) {
        missing.push('author');
      }
      if (productImages.length === 0 || !mainImageUrl) missing.push('images');
    }
    

    if (missing.length > 0) {
      const first = missing[0];
      setInvalidField(first);
      // transient highlight + tooltip for UX
      setHighlightField(first);
      setTooltipVisible(true);
      // Scroll and focus the first missing field
      setTimeout(() => {
        try {
          let el: HTMLElement | null = null;
          if (first === 'images') el = imagesSectionRef.current;
          else el = document.getElementById(`field-${first}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusable = (el.querySelector && el.querySelector('input,textarea,button,select')) as HTMLElement | null;
            if (focusable) focusable.focus();
          }
        } catch (e) {}
      }, 80);
      toast({ title: "Error", description: "Por favor, completa todos los campos requeridos.", variant: "destructive" });
      return;
    }
    
    // Normalize and validate rental options before submit
    const normalizedRentalOptions = rentalOptions
      .map(o => ({ days: Math.max(1, Math.floor(Number(o.days) || 1)), price: o.price ? parseFloat(String(o.price).replace(/[^0-9]/g, '')) : null }))
      .filter(o => o.price !== null && !isNaN(o.price) && o.price >= 0 && o.days >= 1);

    // If user has entered invalid entries (e.g. negative price, days <1) inform and abort
    const hasInvalid = rentalOptions.some(o => {
      const p = o.price ? Number(String(o.price).replace(/[^0-9]/g, '')) : null;
      const d = Number(o.days);
      return (p !== null && (isNaN(p) || p < 0)) || (isNaN(d) || d < 1);
    });
    if (hasInvalid) {
      toast({ title: 'Error', description: 'Revisa las opciones de alquiler: precio inválido o días < 1.', variant: 'destructive' });
      return;
    }

    const productData: Omit<ProductItem, 'id' | 'created_at' | 'updated_at' | 'seller_id' | 'profiles' | 'current_bid' | 'highest_bidder_id'> = {
      title,
      store_id: selectedStoreId,
      author: productType === 'book' ? author : null,
      description: description.trim() === '<p></p>' ? null : description,
      cover_url: mainImageUrl,
      fixed_rental_price: isAuction ? null : (fixedRentalPrice ? parseFloat(fixedRentalPrice) : null),
      fixed_sale_price: isAuction ? null : (fixedSalePrice ? parseFloat(fixedSalePrice) : null),
      free_shipping: freeShipping,
      is_auction: isAuction,
      auction_start_time: auctionStartTime?.toISOString() || null,
      auction_end_time: auctionEndTime?.toISOString() || null,
      starting_bid: startingBid ? parseFloat(startingBid) : null,
      buy_now_price: buyNowPrice ? parseFloat(buyNowPrice) : null,
      product_type: productType,
      condition: condition,
      image_urls: productImages,
      main_image_url: mainImageUrl,
      status: initialProduct?.status || 'available',
      departure_time: productType === 'tours' ? departureTime : null,
      details: details,
      latitude,
      longitude,
      location_name: locationName,
    };

    // Add weight to productData (custom field for shipping/delivery)
    if (weight && parseFloat(weight) > 0) {
      (productData as any).weight = parseFloat(weight);
    }

    // Include departure_time as a top-level column for tours
    if (productType === 'tours') {
      (productData as any).departure_time = departureTime || null;
      // Also keep in details as a backup/history, and include gmt_offset there
      (productData as any).details = {
        ...details,
        departure_time: departureTime || null,
        gmt_offset: longitude !== null ? Math.round(longitude / 15) : null
      };
    }

    // Include brand/model as top-level columns when product is a vehicle
    if (productType === 'vehicle') {
      const vehicle = details.vehicle || {};
      (productData as any).brand = vehicle.brand || null;
      (productData as any).model = vehicle.model || null;
    }

    // Ensure we never submit a fully unset location when the user's profile has a relevant location
    const latToUse = latitude !== null && latitude !== undefined ? latitude : (profile?.relevant_latitude ?? null);
    const lonToUse = longitude !== null && longitude !== undefined ? longitude : (profile?.relevant_longitude ?? null);
    const nameToUse = locationName || profile?.relevant_location_display_name || null;

    productData.latitude = latToUse;
    productData.longitude = lonToUse;
    productData.location_name = nameToUse;

    // Attach validated rental options as a top-level column (dedicated column `rental_options`)
    (productData as any).rental_options = normalizedRentalOptions;

    // Persist stock on the product record itself (all product types)
    const parsedStock = storeStock ? (Number(storeStock) || 0) : null;
    if (parsedStock !== null) (productData as any).stock = parsedStock;
    
    // For groceries, also persist price on the product record
    if (productType === 'groceries') {
      const parsedPrice = storePrice ? (Number(storePrice.replace(/[^0-9]/g, '')) || null) : null;
      if (parsedPrice !== null) (productData as any).fixed_sale_price = parsedPrice;
    }

    // Ensure we propagate product_catalog_id when possible before submitting
    try {
      if (catalogData && (catalogData as any).id) {
        (productData as any).product_catalog_id = (catalogData as any).id;
      } else if ((productData as any).details && (productData as any).details.product_catalog_id) {
        (productData as any).product_catalog_id = (productData as any).details.product_catalog_id;
      } else if ((productData as any).details && (productData as any).details.barcode) {
        // Try to resolve catalog id by barcode synchronously before submit
        try {
          const cat = await getCatalogByBarcode(String((productData as any).details.barcode));
          if (cat && cat.id) {
            (productData as any).product_catalog_id = cat.id;
          }
        } catch (e) {
          // resolution failed; continue without catalog id
          console.warn('validateAndSubmit: could not resolve catalog by barcode', e);
        }
      }
    } catch (e) {
      console.warn('validateAndSubmit: error while resolving product_catalog_id', e);
    }

      // Diagnostic: log final productData before sending to onSubmit
      try {
        // Promote brand/model/barcode/product_catalog_id to top-level if present in details or catalogData
        try {
          if (catalogData && (catalogData as any).brand) (productData as any).brand = (catalogData as any).brand;
          if (catalogData && (catalogData as any).model) (productData as any).model = (catalogData as any).model;
          if ((productData as any).details) {
            if ((productData as any).details.brand) (productData as any).brand = (productData as any).details.brand;
            if ((productData as any).details.model) (productData as any).model = (productData as any).details.model;
            if ((productData as any).details.barcode) (productData as any).barcode = (productData as any).details.barcode;
            if ((productData as any).details.product_catalog_id) (productData as any).product_catalog_id = (productData as any).details.product_catalog_id;
          }
        } catch (e) {}
        console.debug('validateAndSubmit: productData BEFORE onSubmit', productData);
      } catch (e) {
        // ignore logging errors
      }

    // Attach selected store id (nullable) so callers can pass it to createProduct
    try {
      (productData as any).store_id = selectedStoreId ?? null;
    } catch (e) {}

    await onSubmit(productData, imagesToDelete);
    setInvalidField(null);
    setHighlightField(null);
    setTooltipVisible(false);
  };

  

  const handleSubmit = () => {
    // Confirmation logic remains the same
    validateAndSubmit();
  };

  // Determinar si se debe renderizar todo (singleStep) o por pasos
  const showSection = (step: number) => {
    if (singleStep) return true;
    if (typeof activeStep === 'undefined') return true;
    return activeStep === step;
  };

  return (
    <CardContent className="p-6 pt-4 space-y-4">
      {/* Sección de lector de código de barras, siempre visible al inicio */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Lector de Código de Barras
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BarcodeScanner
            onDetected={(code) => {
              setDetails(prev => ({ ...prev, barcode: code }));
              setTitleLockedFromBarcode(true);
              toast({ title: 'Código detectado', description: code });
            }}
          />
        </CardContent>
      </Card>
      {/* Sección 1: Información básica */}
      {showSection(1) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Información Básica del Producto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!hideTitleInput && productType !== 'groceries' && (
              <div id="field-title" className={`grid gap-2 ${highlightField === 'title' ? 'ring-2 ring-primary rounded-md' : ''}`}>
                <Label htmlFor="product-title">Título del Producto</Label>
                {productType === 'other' ? (
                  <div>
                    <ProductTypeahead
                      value={title}
                      onChange={(v) => { setTitle(v); if (invalidField === 'title') setInvalidField(null); if (highlightField === 'title') { setHighlightField(null); setTooltipVisible(false); } }}
                      onSelect={(s) => {
                        setTitle(s.title || '');
                        if (s.brand) setDetails(prev => ({ ...prev, brand: s.brand }));
                      }}
                      onCreateNew={(q) => { setShowNewProductWizard(true); setNewWizardQuery(q); }}
                      onOpenBarcodeScanner={() => {
                        setTitleLockedFromBarcode(false);
                        setShowBarcodeScanner(true);
                      }}
                      isLockedFromBarcode={titleLockedFromBarcode}
                    />
                  </div>
                ) : (
                  <>
                    <Input id="product-title" value={title} onChange={(e) => { setTitle(e.target.value); if (invalidField === 'title') setInvalidField(null); if (highlightField === 'title') { setHighlightField(null); setTooltipVisible(false); } }} disabled={loading} required={productType !== 'groceries' && !hideTitleInput} />
                    {tooltipVisible && highlightField === 'title' ? (
                      <div className="mt-1 text-sm text-destructive transition-opacity duration-200">Por favor ingresa el título del producto.</div>
                    ) : null}
                  </>
                )}
              </div>
            )}

            {['electronics', 'clothing'].includes(productType) && (
              <div className="grid grid-cols-2 gap-2">
                <div className="grid gap-2">
                  <Label htmlFor="product-brand">Marca</Label>
                  <Input
                    id="product-brand"
                    type="text"
                    placeholder="Marca"
                    value={details.brand || ''}
                    onChange={(e) => setDetails(prev => ({ ...prev, brand: e.target.value }))}
                    disabled={loading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="product-model">Modelo</Label>
                  <Input
                    id="product-model"
                    type="text"
                    placeholder="Modelo"
                    value={details.model || ''}
                    onChange={(e) => setDetails(prev => ({ ...prev, model: e.target.value }))}
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {productType === 'book' && (
              <div id="field-author" className={`grid gap-2 ${highlightField === 'author' ? 'ring-2 ring-primary rounded-md' : ''}`}>
                <Label htmlFor="product-author">Autor</Label>
                <Input id="product-author" value={author || ''} onChange={(e) => { setAuthor(e.target.value); if (invalidField === 'author') setInvalidField(null); if (highlightField === 'author') { setHighlightField(null); setTooltipVisible(false); } }} disabled={loading} />
                {tooltipVisible && highlightField === 'author' ? (
                  <div className="mt-1 text-sm text-destructive transition-opacity duration-200">Por favor indica el autor del libro.</div>
                ) : null}
              </div>
            )}

            {productType === 'groceries' && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="product-brand">Marca</Label>
                  <BrandAutocomplete
                    value={details.brand || ''}
                    onChange={(v) => setDetails(prev => ({ ...prev, brand: v }))}
                    onSelect={(b) => setDetails(prev => ({ ...prev, brand: b }))}
                    disabled={loading || titleLockedFromBarcode || isCatalogProduct}
                    placeholder="Empieza a escribir la marca"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="product-model">Modelo</Label>
                  <ModelAutocomplete
                    value={details.model || ''}
                    onChange={(v) => setDetails(prev => ({ ...prev, model: v }))}
                    onSelect={(v) => setDetails(prev => ({ ...prev, model: v.model || v }))}
                    disabled={loading || titleLockedFromBarcode || isCatalogProduct}
                    brandFilter={details.brand || null}
                    placeholder="Empieza a escribir el modelo"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="product-content">Contenido</Label>
                  <div className="flex items-center gap-2">
                    <input
                      id="product-content"
                      className="flex h-10 w-32 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      type="number"
                      min="0"
                      value={contentQuantity}
                      onChange={(e) => setContentQuantity(e.target.value.replace(/[^0-9.]/g, ''))}
                      disabled={loading || titleLockedFromBarcode || isCatalogProduct}
                      placeholder="Cantidad"
                    />

                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <Checkbox id="unit-gr" checked={contentUnit === 'gr'} onCheckedChange={(c) => { if (c) setContentUnit('gr'); else setContentUnit(null); }} disabled={loading || titleLockedFromBarcode || isCatalogProduct} />
                        <Label htmlFor="unit-gr" className="ml-2">gr</Label>
                      </div>

                      <div className="flex items-center">
                        <Checkbox id="unit-ml" checked={contentUnit === 'ml'} onCheckedChange={(c) => { if (c) setContentUnit('ml'); else setContentUnit(null); }} disabled={loading || titleLockedFromBarcode || isCatalogProduct} />
                        <Label htmlFor="unit-ml" className="ml-2">ml</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Store selector: choose which store (if any) to list this product in */}
      {(activeStep === undefined || activeStep === 1) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Vender en
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedStoreId ?? '__none__'} onValueChange={(v) => setSelectedStoreId(v === '__none__' ? null : v)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={userStores && userStores.length > 0 ? 'Seleccionar tienda (por defecto: ninguna)' : 'Sin tienda (perfil)'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin tienda (perfil)</SelectItem>
                {userStores.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Product type selector removed from UI per request */}

      {/* Product Location Display */}
      {showSection(1) && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Ubicación del Producto
          </CardTitle>
        </CardHeader>
        <CardContent>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {!showLocationEditor ? (
              <button
                type="button"
                className="w-full text-left text-sm text-muted-foreground mt-1 px-2 py-1 border border-transparent rounded-md hover:bg-muted"
                onClick={() => {
                  // Blank the editing buffer and open inline editor with autofocus
                  setEditingLocationName(null);
                  setEditingLatitude(null);
                  setEditingLongitude(null);
                  setShowLocationEditor(true);
                  setLocationEditorAutoFocus(true);
                }}
              >
                {(() => {
                  const effectiveName = locationName || profile?.relevant_location_display_name || null;
                  if (effectiveName) {
                    return effectiveName;
                  }

                  return 'Ubicación no establecida';
                })()}
              </button>
            ) : null}
          </div>
          <div className="ml-2">
            {!showLocationEditor ? (
              <Button size="sm" variant="ghost" onClick={() => { setShowLocationEditor(true); setLocationEditorAutoFocus(true); setEditingLocationName(null); setEditingLatitude(null); setEditingLongitude(null); }}>
                Editar
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button size="sm" variant="ghost" onClick={() => { setShowLocationEditor(false); setLocationEditorAutoFocus(false); }}>Cerrar</Button>
              </div>
            )}
          </div>
        </div>

        {showLocationEditor && (
          <div className="mt-2 flex items-start space-x-2">
            <div className="flex-1">
              <LocationInput
                autoFocus={locationEditorAutoFocus}
                forceBlank={locationEditorAutoFocus}
                showLabel={false}
                value={editingLatitude !== null && editingLongitude !== null ? { displayName: editingLocationName || `Lat: ${editingLatitude.toFixed(4)}, Lon: ${editingLongitude.toFixed(4)}`, latitude: editingLatitude, longitude: editingLongitude } as NoteLocation : (
                  // when editing buffer is blank, show current effective value as starting value in the picker
                  (latitude !== null && longitude !== null) ? { displayName: locationName || `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`, latitude, longitude } as NoteLocation : (
                    profile?.relevant_latitude != null && profile?.relevant_longitude != null ? { displayName: profile?.relevant_location_display_name || `Lat: ${profile.relevant_latitude.toFixed(4)}, Lon: ${profile.relevant_longitude.toFixed(4)}`, latitude: profile.relevant_latitude, longitude: profile.relevant_longitude } as NoteLocation : null
                  )
                )}
                onChange={(loc) => {
                  if (!loc) {
                    setEditingLocationName(null);
                    setEditingLatitude(null);
                    setEditingLongitude(null);
                  } else {
                    setEditingLocationName(loc.displayName || null);
                    setEditingLatitude(loc.latitude);
                    setEditingLongitude(loc.longitude);
                  }

                  // Disable the one-time autofocus/forceBlank once the user interacts
                  setLocationEditorAutoFocus(false);
                }}
                disabled={loading}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                onClick={async () => {
                  // Save edited values (use fallback to profile if unset)
                  const newLat = editingLatitude !== null && editingLatitude !== undefined ? editingLatitude : (profile?.relevant_latitude ?? null);
                  const newLon = editingLongitude !== null && editingLongitude !== undefined ? editingLongitude : (profile?.relevant_longitude ?? null);
                  const newName = editingLocationName || profile?.relevant_location_display_name || null;

                  setLocationName(newName);
                  setLatitude(newLat);
                  setLongitude(newLon);
                  setShowLocationEditor(false);
                  setLocationEditorAutoFocus(false);

                  if (initialProduct && session?.user?.id) {
                    try {
                      const updated = await updateProduct(initialProduct.id, session.user.id, {
                        latitude: newLat,
                        longitude: newLon,
                        location_name: newName,
                      } as any);

                      // Re-fetch and verify
                      try {
                        const refetched = await getProductById(initialProduct.id);

                        const fetchedLat = refetched?.latitude ?? null;
                        const fetchedLon = refetched?.longitude ?? null;
                        const fetchedName = refetched?.location_name ?? null;

                        if (
                          (newLat === null && fetchedLat === null) ||
                          (newLat !== null && fetchedLat !== null && Math.abs(newLat - fetchedLat) < 0.0001)
                        ) {
                          toast({ title: 'Ubicación guardada', description: 'La ubicación del producto se actualizó correctamente.' });
                        } else {
                          console.error('Persistence mismatch after save', { newLat, fetchedLat, newLon, fetchedLon, newName, fetchedName });
                          toast({ title: 'Error de persistencia', description: 'La actualización se envió pero no se guardó en el servidor. Revisa la consola para más detalles.', variant: 'destructive' });
                        }
                      } catch (refetchErr) {
                        console.error('Error refetching product after save:', refetchErr);
                        toast({ title: 'Advertencia', description: 'Se guardó la actualización, pero no se pudo verificar en el servidor.', variant: 'default' });
                      }
                    } catch (err) {
                      console.error('Error saving product location:', err);
                      toast({ title: 'Error', description: 'No se pudo guardar la ubicación. Intenta nuevamente.', variant: 'destructive' });
                    }
                  }
                }}
                disabled={loading}
              >Guardar</Button>
              <Button size="sm" variant="ghost" onClick={() => {
                // Revert editing buffer and close
                setEditingLocationName(null);
                setEditingLatitude(null);
                setEditingLongitude(null);
                setShowLocationEditor(false);
                setLocationEditorAutoFocus(false);
              }}>Cancelar</Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>
      )}

      {/* Removed redundant barcode input modal; keeping scanner and wizard modals active */}

      {showNewProductWizard && (
        <NewProductWizard
          initialTitle={newWizardQuery || undefined}
          initialBarcode={newWizardQuery && /^[0-9]{6,}$/i.test(newWizardQuery) ? newWizardQuery : undefined}
          onClose={() => { setShowNewProductWizard(false); setNewWizardQuery(null); setBarcodeModalInput(''); }}
            onCreated={(entry) => {
            setShowNewProductWizard(false);
            setTitle(entry?.title || '');
            if (entry?.brand) setDetails(prev => ({ ...prev, brand: entry.brand }));
            if (entry?.model) setDetails(prev => ({ ...prev, model: entry.model }));
              if (entry?.barcode) setDetails(prev => ({ ...prev, barcode: entry.barcode }));
            // Prefill and parse content (e.g., "200gr" or "500ml")
            if (entry?.content) {
              const match = (entry.content || '').match(/^\s*(\d+(?:\.\d+)?)\s*(gr|ml)\s*$/i);
              if (match) {
                setContentQuantity(match[1]);
                setContentUnit(match[2].toLowerCase() === 'ml' ? 'ml' : 'gr');
              } else {
                setContentQuantity(entry.content || '');
              }
            }
            // Mirror barcode-recognition behavior: lock title/content/model from edits
            setTitleLockedFromBarcode(true);
            if (entry?.product_type) setProductType(entry.product_type);
            toast({ title: 'Creado', description: 'Producto creado en el catálogo.' });
          }}
        />
      )}
      {showBarcodeScanner && (
        <BarcodeScannerModal
          onClose={() => setShowBarcodeScanner(false)}
          onDetected={(code) => {
            setShowBarcodeScanner(false);
            setTitle(code);
            // try to lookup and prefill
            (async () => {
              try {
                const catalog = await getCatalogByBarcode(code);
                if (catalog) {
                  setCatalogData(catalog);
                  setTitle(catalog.title || code);
                  setTitleLockedFromBarcode(true);
                  // persist barcode into details so it's stored on create
                  setDetails(prev => ({ ...prev, barcode: code }));
                  if (catalog.brand) setDetails(prev => ({ ...prev, brand: catalog.brand }));
                  if (catalog.model) setDetails(prev => ({ ...prev, model: catalog.model }));
                  // Prefill content (cantidad + unidad) when present in catalog
                  if (catalog.content) {
                    const match = (catalog.content || '').match(/^\s*(\d+(?:\.\d+)?)\s*(gr|ml)\s*$/i);
                    if (match) {
                      setContentQuantity(match[1]);
                      setContentUnit(match[2].toLowerCase() === 'ml' ? 'ml' : 'gr');
                    } else {
                      setContentQuantity(catalog.content || '');
                    }
                  }
                  // Show toast with product title bold and message below
                  toast({
                    title: (<span><strong>{catalog.title || code}</strong></span>) as any,
                    description: 'Se encontró el producto en el catálogo.'
                  });

                  // UI-only: animate scroll to images if available, otherwise to availability
                  try {
                    const hasImages = Array.isArray(catalog.image_urls) && catalog.image_urls.length > 0;
                    if (hasImages && imagesSectionRef.current) {
                      imagesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else if (availabilitySectionRef.current) {
                      availabilitySectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  } catch (e) {
                    // ignore UI scroll errors
                  }
                } else {
                  setTitleLockedFromBarcode(false);
                  setNewWizardQuery(code);
                  setShowNewProductWizard(true);
                }
              } catch (e) {
                console.error('scanner lookup error', e);
                setTitleLockedFromBarcode(false);
                toast({ title: 'Error', description: 'No se pudo buscar el código.' });
              }
            })();
          }}
        />
      )}


      {showSection(3) && productType === 'vehicle' && (
        <VehicleDetailsForm details={details.vehicle || {}} setDetails={(d) => setDetails(prev => ({ ...prev, vehicle: d }))} loading={loading} />
      )}
      {showSection(3) && productType === 'electronics' && (
        <ElectronicsDetailsForm details={details.electronics || {}} setDetails={(d) => setDetails({ ...details, electronics: d })} loading={loading} noAutocomplete />
      )}
      {showSection(3) && productType === 'clases_particulares' && (
        <PrivateClassesFields details={details} onDetailsChange={(d) => setDetails(d)} />
      )}

      {showSection(1) && productType === 'tours' && (
        <div className="p-4 border border-border rounded-lg bg-card mt-4">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Detalles de la Excursión
          </h3>
          <div className="grid gap-2">
            <Label htmlFor="departure-time" className="text-sm font-medium">Hora de salida</Label>
            <div className="flex items-center gap-3">
              <Input
                id="departure-time"
                type="time"
                value={departureTime}
                onChange={(e) => setDepartureTime(e.target.value)}
                disabled={loading}
                className="w-full max-w-[200px]"
              />
              {latitude !== null && longitude !== null && (
                <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                  GMT: {Math.round(longitude / 15) >= 0 ? `+${Math.round(longitude / 15)}` : Math.round(longitude / 15)} (calculado según ubicación)
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showSection(1) && (
        <div className="p-4 border border-border rounded-lg bg-card">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Info className="h-4 w-4" />
            Condición del Producto
          </h3>
          <div className="grid gap-2">
            <Label htmlFor="product-condition" className="text-sm font-medium">Estado del artículo</Label>
            <Select
              value={productType === 'groceries' ? 'new' : (condition || '')}
              onValueChange={setCondition}
              disabled={loading || productType === 'groceries'}
            >
              <SelectTrigger id="product-condition"><SelectValue placeholder="Selecciona la condición" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Nuevo</SelectItem>
                <SelectItem value="like_new">Como Nuevo</SelectItem>
                <SelectItem value="used_good">Usado (Buen estado)</SelectItem>
                <SelectItem value="used_fair">Usado (Estado regular)</SelectItem>
                <SelectItem value="for_parts">Para piezas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Sección de Imágenes */}
      {showSection(2) && (
        <div className="p-4 border border-border rounded-lg bg-card">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Image className="h-4 w-4" />
            Imágenes del Producto
          </h3>
          <div id="field-images" className={`${highlightField === 'images' ? 'ring-2 ring-primary rounded-md' : ''}`} ref={imagesSectionRef}>
            <ProductImageManager 
              session={session} 
              initialImageUrls={productImages} 
              initialMainImageUrl={mainImageUrl} 
              onImagesChange={handleImagesChange} 
              disabled={loading || (isCatalogProduct && productType === 'groceries')} 
            />
            {tooltipVisible && highlightField === 'images' ? (
              <div className="mt-1 text-sm text-destructive transition-opacity duration-200">Agrega al menos una imagen principal para el producto.</div>
            ) : null}
          </div>
        </div>
      )}

      {/* Sección de Descripción */}
      {showSection(2) && (
        <div className="p-4 border border-border rounded-lg bg-card">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Descripción del Producto
          </h3>
          <RichTextEditor 
            content={description} 
            onChange={setDescription} 
            disabled={loading || (isCatalogProduct && productType === 'groceries')} 
          />
        </div>
      )}

      {/* SECCIÓN 1: Stock y Disponibilidad */}
      {showSection(4) && (
        <div className="p-4 border border-border rounded-lg bg-card" ref={availabilitySectionRef}>
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Package className="h-4 w-4" />
            Stock Disponible
          </h3>
          <div className="grid gap-2">
            <label htmlFor="store-stock" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Cantidad en inventario</label>
            <input
              id="store-stock"
              type="number"
              min="0"
              value={storeStock !== '' && storeStock !== null && storeStock !== undefined ? String(Number(storeStock)) : (typeof initialProduct !== 'undefined' && initialProduct !== null && typeof initialProduct.stock !== 'undefined' && initialProduct.stock !== null ? String(Number(initialProduct.stock)) : '')}
              onChange={(e) => { setStoreStock(e.target.value); }}
              placeholder="Cantidad"
              disabled={loading}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm tabular-nums"
            />
          </div>
        </div>
      )}

      {/* SECCIÓN 2: Método de Venta */}
      {showSection(4) && productType !== 'groceries' && (
        <div className="p-4 border border-border rounded-lg bg-card">
          <h3 className="text-base font-semibold mb-3">Método de Venta</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <button
              type="button"
              role="radio"
              aria-checked={!isAuction}
              onClick={() => setIsAuction(false)}
              disabled={loading}
              className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                !isAuction 
                  ? 'bg-primary text-primary-foreground border-primary shadow-md' 
                  : 'bg-background border-muted hover:border-primary hover:bg-primary/5'
              }`}
            >
              <DollarSign className={`h-6 w-6 mb-2 ${!isAuction ? 'text-primary-foreground' : 'text-foreground'}`} />
              <span className={`text-sm font-medium ${!isAuction ? 'text-primary-foreground' : 'text-foreground'}`}>Precio Fijo</span>
              <span className={`text-xs mt-1 ${!isAuction ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>Venta o alquiler directo</span>
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={isAuction}
              onClick={() => setIsAuction(true)}
              disabled={loading}
              className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isAuction 
                  ? 'bg-primary text-primary-foreground border-primary shadow-md' 
                  : 'bg-background border-muted hover:border-primary hover:bg-primary/5'
              }`}
            >
              <Gavel className={`h-6 w-6 mb-2 ${isAuction ? 'text-primary-foreground' : 'text-foreground'}`} />
              <span className={`text-sm font-medium ${isAuction ? 'text-primary-foreground' : 'text-foreground'}`}>Subasta</span>
              <span className={`text-xs mt-1 ${isAuction ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>Los usuarios pujan por el producto</span>
            </button>
          </div>

          {isAuction ? (
            <AuctionFields
              startingBid={startingBid}
              setStartingBid={setStartingBid}
              buyNowPrice={buyNowPrice}
              setBuyNowPrice={setBuyNowPrice}
              auctionStartTime={auctionStartTime}
              setAuctionStartTime={setAuctionStartTime}
              auctionEndTime={auctionEndTime}
              setAuctionEndTime={setAuctionEndTime}
              loading={loading}
              handlePriceInputChange={handlePriceInputChange}
            />
          ) : (
            <FixedPriceFields
              fixedRentalPrice={fixedRentalPrice}
              setFixedRentalPrice={setFixedRentalPrice}
              fixedSalePrice={fixedSalePrice}
              setFixedSalePrice={setFixedSalePrice}
              loading={loading}
              handlePriceInputChange={handlePriceInputChange}
              rentalOptions={rentalOptions}
              setRentalOptions={setRentalOptions}
            />
          )}
        </div>
      )}

      {/* SECCIÓN 3: Opciones de Envío */}
      {showSection(4) && (
        <Card className="hover:border-primary transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Truck className="h-5 w-5 text-primary" />
              Opciones de Envío
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox id="free-shipping" checked={freeShipping} onCheckedChange={(c) => setFreeShipping(c as boolean)} disabled={loading} />
              <Label htmlFor="free-shipping" className="text-sm font-normal">Ofrecer envío gratis a los compradores</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight" className="text-sm font-medium">Peso del Producto</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="0.0"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  disabled={loading}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground min-w-[2rem]">kg</span>
              </div>
              {weight && parseFloat(weight) > 0 && (
                <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                  <span className="text-xs text-muted-foreground">
                    Vehículo requerido:
                  </span>
                  <span className="text-xs font-medium text-primary">
                    {getRequiredTransportType(parseFloat(weight)) === 'walking' && '🚶 A pie'}
                    {getRequiredTransportType(parseFloat(weight)) === 'bike' && '🚲 Bicicleta'}
                    {getRequiredTransportType(parseFloat(weight)) === 'motorcycle' && '🏍️ Moto'}
                    {getRequiredTransportType(parseFloat(weight)) === 'car' && '🚗 Auto'}
                    {getRequiredTransportType(parseFloat(weight)) === 'van' && '🚐 Camioneta'}
                  </span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                El peso determina el tipo de vehículo requerido para el envío
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {showSection(4) && (
        <>

          

          <Button onClick={handleSubmit} disabled={loading} className="w-full">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : submitButtonText}
          </Button>

          <AlertDialog open={showConfirmationDialog} onOpenChange={setShowConfirmationDialog}>
            <AlertDialogContent
              aria-labelledby="product-form-confirm-title"
              aria-describedby="product-form-confirm-description"
            >
              <AlertDialogHeader>
                <AlertDialogTitle id="product-form-confirm-title">Confirmar Acción</AlertDialogTitle>
                <AlertDialogDescription id="product-form-confirm-description">{confirmationMessage}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={validateAndSubmit}>Confirmar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </CardContent>
  );
};

export default ProductForm;